import _ from "lodash";
/**--external-- */
import React, { useState } from "react";
import PropTypes from "prop-types";
import { IconButton, Button } from "@toddle-edu/ds-web";
import { ChevronLeftOutlined, EyeOutlined } from "@toddle-edu/ds-icons";
import classNames from "classnames";
import { connect } from "react-redux";

/**--internal-- */
import { useI18n } from "Hooks";
import { ToddleIcon } from "SvgComponents";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import DialogueBoxV2 from "UIComponents/DialogueBoxV2";

/**--relative-- */
import classes from "./Header.scss";
import { styles } from "./HeaderStyles";

const Header = props => {
  const { t } = useI18n(["common"]);

  const [showDiscardChangesModal, setShowDiscardChangesModal] = useState(false);
  const {
    title,
    isBackButtonDisabled,
    isSaveButtonDisabled,
    currentEditorConfig,
    nodes,
    setNodes,
    setToastMsg,
    mode,
    onClickPreviewButton,
  } = props;

  const titleContainerClasses = classNames(classes.titleContainer, "heading-6");

  const checkIsValid = () => {
    let isValid = true;
    const errorCheckFields = _.get(currentEditorConfig, "errorCheckFields", []);
    let nodeToScrollTo;
    const newNodes = { ...nodes };
    _.forOwn(newNodes, (value, key) => {
      if (value.parentId) {
        for (const field of errorCheckFields) {
          const currentFieldValue = value[field];
          if (_.isEmpty(currentFieldValue) && value.depth != 0) {
            newNodes[key] = { ...value, isError: true };
            isValid = false;
            if (!nodeToScrollTo) nodeToScrollTo = key;
            break;
          }
          newNodes[key] = { ...value, isError: false };
        }
      }
    });
    if (isValid) {
      return true;
    }

    setNodes(newNodes);

    //We are using getElementById as we don't have refs of all nodes.
    const node = document.getElementById(nodeToScrollTo);
    if (node) node.scrollIntoView({ block: "center" });

    return false;
  };

  const onSaveClick = () => {
    const areNodesValid = checkIsValid();
    if (!areNodesValid) {
      setToastMsg({ msg: "toastMsgs:sns_errors_on_save" });
      return;
    }

    props.onSaveClick();
  };

  const onBackClick = () => {
    mode === "edit" ? setShowDiscardChangesModal(true) : props.onBackClick();
  };

  const onDiscardChanges = () => {
    setShowDiscardChangesModal(false);
    props.onBackClick();
  };

  const onContinueEditing = () => {
    setShowDiscardChangesModal(false);
  };

  return (
    <React.Fragment>
      <div className={classes.container}>
        <div className={classes.headingContainer}>
          <IconButton
            variant={"plain"}
            icon={<ChevronLeftOutlined />}
            onClick={onBackClick}
            disabled={isBackButtonDisabled}
          />

          <div className={classes.toddleIconContainer}>
            <ToddleIcon width="32px" height="32px" />
          </div>
          <div className={titleContainerClasses}>{title}</div>
        </div>
        {mode === "edit" ? (
          <div className={classes.buttonsContainer}>
            <Button
              variant="outlined"
              size="large"
              onClick={onClickPreviewButton}
              icon={<EyeOutlined />}
            >
              {t("common:preview")}
            </Button>
            <Button
              variant="progressive"
              size="large"
              onClick={onSaveClick}
              disabled={isSaveButtonDisabled}
            >
              {t("common:save")}
            </Button>
          </div>
        ) : null}
      </div>
      {showDiscardChangesModal ? (
        <DialogueBoxV2
          modalTitle={t("common:you_have_unsaved_changes")}
          showModal
          onClickButton1={onContinueEditing}
          onClickButton2={onDiscardChanges}
          toggleDialogueBoxDisplay={onContinueEditing}
          modalBody={t("common:all_changes_will_be_discarded")}
          button1={t("common:cancel")}
          button2={t("common:discard_changes")}
          size="auto"
          closeButtonSize="medium"
          customModalContentClass={classes.modalContent}
          button1Props={{
            size: "large",
            variant: "outlined-subtle",
          }}
          button2Props={{
            size: "large",
          }}
          headerStyles={styles.headerStyles}
          modalBodyStyle={styles.modalBodyStyle}
          footerContainerStyle={styles.footerContainerStyle}
        />
      ) : null}
    </React.Fragment>
  );
};

const mapActionCreators = { setToastMsg };

export default connect(null, mapActionCreators)(Header);

Header.propTypes = {
  onBackClick: PropTypes.func.isRequired,
  onSaveClick: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  isBackButtonDisabled: PropTypes.bool,
  isSaveButtonDisabled: PropTypes.bool,
  currentEditorConfig: PropTypes.object.isRequired,
  nodes: PropTypes.object.isRequired,
  setNodes: PropTypes.func.isRequired,
};
